'use client';

import React from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Box,
  Typography,
  MenuItem,
  Popover,
  Pagination,
  Chip,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { ChevronLeft, ChevronRight, MapOutline } from 'mdi-material-ui';
import { MuiButton } from '@/components/button/MuiButton';
import { RHFSelect } from '@/components/select/RHFSelect';
import { SearchBox } from '@/features/station/SearchBox';
import { useLogic } from './logic';
import { STATION_SORTS } from '@/types/types';
import { api_portalOfficeListing_filter } from '@/init/swagger';
import { RequireLoginButton } from '@/features/hospital/HospitalSearchFilters/RequireLoginButton';
import { PublicListingList } from '@/features/station/PublicListingList';
import { PublicStationMap } from '@/features/station/PublicStationMap';
import { ParsedPublicStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/public/stations/search/Schema';
import { service_ResponseForNonMemberPortalOfficeListing } from '@/init/swagger';
import { ServerErrorInfo } from '@/helpers/serverErrorSnackbarHelper';
import { ServerErrorSnackbar } from '@/features/hospital/ServerErrorSnackbar';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PublicSearchFilterBox } from '@/features/station/PublicSearchFilterBox';

type Props = {
  searchFilters: api_portalOfficeListing_filter[];
  parsedSearchParams: ParsedPublicStationsSearchPageSearchParams;
  portalOfficeListings?: service_ResponseForNonMemberPortalOfficeListing[];
  allPortalOfficeListingTotalCount?: number;
  autoTargetAddressTextByPostCode?: string;
  serverError?: ServerErrorInfo | null;
};

export const StationsPublicSearchPage: React.FC<Props> = ({
  searchFilters,
  parsedSearchParams,
  portalOfficeListings,
  allPortalOfficeListingTotalCount,
  autoTargetAddressTextByPostCode,
  serverError,
}) => {
  const {
    formMethod,
    listings,
    searchFilterEl,
    isShowMap,
    targetAddress,
    handleSearch,
    handleOpenSearchFilter,
    handleCloseSearchFilter,
    handleToggleMap,
    isMobile,
    paginationInfo,
    gotoPageForPagination,
    onChangePagination,
    hoveredMarkerListingId,
    setHoveredMarkerListingIdWithoutScroll,
    handleMarkerClick,
    listingListRef,
    searchFilterType,
    selectedServiceAndAdditionStatusCount,
    selectedServiceAndAdditionStatusText,
    selectedSearchFilters,
  } = useLogic({
    searchFilters,
    parsedSearchParams,
    portalOfficeListings,
    allPortalOfficeListingTotalCount,
    autoTargetAddressTextByPostCode,
    serverError,
  });

  return (
    <Box>
      <FormProvider {...formMethod}>
        <form onSubmit={formMethod.handleSubmit(handleSearch)}>
          <Box
            sx={{
              background: '#F5FAFA',
              px: {
                sm: 5,
                lg: 15,
              },
              py: 2,
              '@media (max-width: 800px)': {
                px: 2,
              },
            }}
          >
            <SearchBox formMethod={formMethod} isPublicSearch={true} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              px: {
                sm: 5,
                lg: 15,
              },
              gap: 3,
              '@media (max-width: 800px)': {
                px: 2,
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                pt: 3,
                pb: 3,
                '@media (max-width: 800px)': {
                  display: '',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',

                  '@media (max-width: 740px)': {
                    flexDirection: 'column',
                  },
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <MuiButton
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={(ev) =>
                        handleOpenSearchFilter(ev, 'serviceAndAdditionStatus')
                      }
                      variant="outlined"
                      color={
                        selectedServiceAndAdditionStatusCount()
                          ? 'primary'
                          : 'inherit'
                      }
                      sx={{
                        width: '100%',
                        minWidth: 160,
                        height: 44,
                        padding: '4px 8px',
                      }}
                    >
                      <Typography
                        variant="JP/Body/m/Regular/Tight"
                        sx={{
                          textAlign: 'left',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          flex: 1,
                        }}
                      >
                        サービス・加算で絞る
                      </Typography>
                      {!!selectedServiceAndAdditionStatusCount() && (
                        <Chip
                          color="primary"
                          size="small"
                          label={selectedServiceAndAdditionStatusCount()}
                        />
                      )}
                    </MuiButton>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        display: '-webkit-box',

                        '@media (max-width: 740px)': {
                          display: 'none',
                        },
                      }}
                    >
                      {selectedServiceAndAdditionStatusText()}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <RequireLoginButton
                      text="施術・保有資格で絞る"
                      onClick={(ev) =>
                        handleOpenSearchFilter(ev, 'requireLogin')
                      }
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <RequireLoginButton
                      text="アピールポイントで絞る"
                      onClick={(ev) =>
                        handleOpenSearchFilter(ev, 'requireLogin')
                      }
                    />
                  </Box>
                </Box>

                <Popover
                  open={!!searchFilterEl}
                  anchorEl={searchFilterEl}
                  onClose={handleCloseSearchFilter}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <PublicSearchFilterBox
                    close={handleCloseSearchFilter}
                    selectedSearchFilters={selectedSearchFilters}
                    searchFilters={searchFilters}
                    searchFilterType={searchFilterType}
                  />
                </Popover>
              </Box>
            </Box>
          </Box>
        </form>
      </FormProvider>

      <Box
        sx={{
          display: 'flex',
          px: {
            sm: 5,
            lg: 15,
          },
          gap: 3,
          '@media (max-width: 800px)': {
            px: isShowMap ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            width: isShowMap ? '50%' : '100%',
            mt: 3,
            '@media (max-width: 800px)': {
              display: isShowMap ? 'none' : '',
              flexDirection: 'column',
              width: isShowMap ? 0 : '100%',
              height: isShowMap ? 0 : '100%',
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <LocationOnOutlinedIcon sx={{ color: '#FFD600' }} />
            <Typography
              variant="JP/Body/m/Regular/Normal"
              sx={{ color: '#595960' }}
            >
              {targetAddress}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              flexWrap: 'wrap',
              gap: 1,
              mb: 3,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                flexBasis: 'max-content',
              }}
            >
              <Typography
                variant="JP/Heading/lg/Bold/Tight"
                sx={{ color: '13131D' }}
              >
                指定した住所周辺の看護・介護サービス
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              <Box>
                <RHFSelect
                  name={formMethod.register('sort').name}
                  control={formMethod.control}
                  placeholder="選択してください"
                  variant="outlined"
                  sx={{
                    m: 0,
                    maxHeight: 32,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#13131D',
                    },
                  }}
                >
                  {STATION_SORTS.map((sst) => {
                    return (
                      <MenuItem key={sst.value} value={sst.value}>
                        {sst.text}
                      </MenuItem>
                    );
                  })}
                </RHFSelect>
              </Box>
              <Box>
                <MuiButton
                  startIcon={<MapOutline />}
                  onClick={handleToggleMap}
                  variant="outlined"
                  color={isShowMap ? 'primary' : 'inherit'}
                  sx={{
                    m: 0,
                    maxHeight: 32,
                  }}
                >
                  Map表示
                </MuiButton>
              </Box>
            </Box>

            <Box>
              <Typography variant="JP/Body/m/Regular/Normal" align="right">
                {!paginationInfo.isShowPagination &&
                  paginationInfo.lastPage !== 0 &&
                  listings && (
                    <>
                      {`全 ${paginationInfo.allListingTotalCount} 件( ${paginationInfo.current.from}〜${paginationInfo.current.to} 件を表示)`}
                    </>
                  )}
              </Typography>
            </Box>
          </Box>

          {paginationInfo.isShowPagination && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: 'wrap',
                gap: 1,
                marginBottom: 3,
              }}
            >
              <Box>
                <Typography variant="JP/Body/m/Regular/Normal">
                  {`${paginationInfo.current.from}〜${paginationInfo.current.to} 件を表示`}
                </Typography>

                <Typography variant="JP/Body/m/Regular/Normal">/</Typography>

                <Typography variant="JP/Body/m/Regular/Normal">
                  {`全 ${paginationInfo.allListingTotalCount} 件`}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <MuiButton
                  startIcon={<ChevronLeft />}
                  onClick={() =>
                    gotoPageForPagination(paginationInfo.current.page - 1)
                  }
                  variant="outlined"
                  sx={{
                    m: 0,
                    p: 1,
                    minWidth: 'auto',
                    maxHeight: 32,

                    ' & .MuiButton-startIcon': {
                      m: 0,
                    },
                  }}
                  disabled={
                    paginationInfo.current.page === 1 ||
                    paginationInfo.pages.length <= 1
                  }
                  aria-label="前のページ"
                />

                <RHFSelect
                  name={formMethod.register('page').name}
                  control={formMethod.control}
                  placeholder="ページ"
                  variant="outlined"
                  sx={{
                    m: 0,
                    width: 'auto',
                    maxHeight: 32,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#13131D',
                    },
                  }}
                >
                  {paginationInfo.pages.map((page) => {
                    return (
                      <MenuItem key={page} value={page}>
                        {page}/{paginationInfo.lastPage}
                      </MenuItem>
                    );
                  })}
                </RHFSelect>

                <MuiButton
                  startIcon={<ChevronRight />}
                  onClick={() =>
                    gotoPageForPagination(paginationInfo.current.page + 1)
                  }
                  variant="outlined"
                  sx={{
                    m: 0,
                    p: 1,
                    minWidth: 'auto',
                    maxHeight: 32,

                    ' & .MuiButton-startIcon': {
                      m: 0,
                    },
                  }}
                  disabled={
                    paginationInfo.current.page === paginationInfo.pages.length
                  }
                  aria-label="次のページ"
                />
              </Box>
            </Box>
          )}

          <Box
            sx={
              isShowMap
                ? {
                    width: '100%',
                    height: 800,
                    padding: 0.5,
                    overflow: 'auto',
                  }
                : {
                    pb: 3,
                  }
            }
          >
            <PublicListingList
              ref={listingListRef}
              listings={listings}
              hoveredMarkerListingId={hoveredMarkerListingId}
              setHoveredMarkerListingIdWithoutScroll={
                setHoveredMarkerListingIdWithoutScroll
              }
              isShowMap={isShowMap}
              searchFilters={searchFilters}
            />

            <Box
              sx={{
                display: paginationInfo.isShowPagination ? 'flex' : 'none',
              }}
            >
              <Pagination
                count={paginationInfo.lastPage}
                page={paginationInfo.current.page}
                siblingCount={3}
                boundaryCount={1}
                onChange={onChangePagination}
              />
            </Box>
          </Box>
        </Box>

        {isShowMap && (
          <Box
            sx={{
              width: isShowMap ? '50%' : '100%',
              '@media (max-width: 800px)': {
                width: isShowMap ? '100%' : 0,
                height: isShowMap ? 'calc(100dvh * 0.62)' : 0,
                marginBottom: isShowMap ? 15 : 0,
              },
            }}
          >
            <PublicStationMap
              listings={listings || []}
              handleToggleMap={handleToggleMap}
              hoveredMarkerListingId={hoveredMarkerListingId}
              onMarkerClick={handleMarkerClick}
            />
          </Box>
        )}
      </Box>
      <ServerErrorSnackbar error={serverError || null} />
    </Box>
  );
};
